import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PromptSnackBar, {
  PromptProps,
  PromptSnackBarProps,
} from "components/common/ui/PromptSnackBar";
import { addApiErrHandler } from "client/portals";
import { isErrorResponseBodyType } from "models/utils/apiUtils";

interface PromptSnackBarContextType extends PromptSnackBarProps {}

export const PromptSnackBarContext = createContext<PromptSnackBarContextType>(
  null!,
);

export const usePromptSnackBar = (): PromptSnackBarContextType => {
  return useContext(PromptSnackBarContext);
};

const PromptSnackBarProvider: FC<PropsWithChildren> = ({ children }) => {
  const [promptProps, setPromptProps] = useState<PromptProps>({
    isOpen: false,
    message: "",
    severity: "info",
    autoHeightDuration: undefined,
    onPromptClose: undefined,
  });

  useEffect(() => {
    addApiErrHandler((response): void => {
      if (response.config?.suppressErrorPrompt) {
        return;
      }
      switch (response.status) {
        case 200:
          break;
        case 422:
          if (isErrorResponseBodyType(response.data)) {
            setPromptProps({
              isOpen: true,
              message: JSON.stringify(response.data.detail),
              severity: "error",
              autoHeightDuration: 5000,
            });
          }
          break;
        default:
          if (isErrorResponseBodyType(response.data)) {
            setPromptProps({
              isOpen: true,
              message: response.data.detail,
              severity: "error",
              autoHeightDuration: undefined,
            });
          }
          break;
      }
    });
  });

  const promptContext = useMemo(
    () => ({ promptProps, setPromptProps }),
    [promptProps],
  );

  return (
    <PromptSnackBarContext.Provider value={promptContext}>
      {children}
      <PromptSnackBar
        promptProps={promptProps}
        setPromptProps={setPromptProps}
      />
    </PromptSnackBarContext.Provider>
  );
};

export default PromptSnackBarProvider;
